import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, IconButton, Button, Dialog, DialogContent, DialogActions,
  Grid, Popover, Typography, TextField, Chip, Tooltip, CircularProgress,
  alpha, InputAdornment, useMediaQuery, useTheme, Stack, Divider, Autocomplete,
  Collapse,Snackbar,Alert,
} from "@mui/material";
import {
  Edit as EditIcon, Delete as DeleteIcon, Print as PrintIcon,
  FilterList as FilterListIcon, Close as CloseIcon, Refresh as RefreshIcon,
  Download as DownloadIcon, Search as SearchIcon, Business as BusinessIcon,
  LocalShipping as TruckIcon, Receipt as ReceiptIcon,
} from "@mui/icons-material";
import { freightsByBranch, branchList } from "../../../Services/Services";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import InfiniteScroll from "react-infinite-scroll-component";

import { brandColor as brand } from "../../../theme";
const brandBg = alpha(brand, 0.06);

const formatDate = (date) => date ? new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const formatCurrency = (amount) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(amount || 0);

export default function BranchFreight() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showFilters, setShowFilters] = useState(false);

  const [data, setData] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [tpFilter, setTpFilter] = useState("");
  const [truckFilter, setTruckFilter] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Fetch branches for dropdown
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const branchData = await branchList();
        if (Array.isArray(branchData)) {
          setBranchOptions(branchData);
        }
      } catch (error) {
        console.error("Error fetching branch list:", error);
        setBranchOptions([]);
      }
    };
    fetchBranches();
  }, []);

  const fetchFreights = useCallback(async (branchId, pageNo = 1, append = false) => {
    if (!branchId) return;
    setLoading(true);
    try {
      const filters = {
        tp_no: tpFilter || undefined,
        truck_no: truckFilter || undefined,
      };
      const res = await freightsByBranch(branchId, pageNo, filters);
      const results = res?.results || [];
      
      if (append) {
        setData(prev => [...prev, ...results]);
      } else {
        setData(results);
      }
      setHasMore(!!res?.next);
      setPage(pageNo + 1);
    } catch (error) {
      console.error("Error fetching freights:", error);
      setData([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [tpFilter, truckFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedBranch) {
        fetchFreights(selectedBranch, 1, false);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [tpFilter, truckFilter, selectedBranch, fetchFreights]);

  const fetchMoreData = async () => {
    if (!selectedBranch || !hasMore || loading) return;
    await fetchFreights(selectedBranch, page, true);
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranch(branchId);
    setData([]);
    setPage(1);
    setHasMore(true);
  };

  const clearFilters = () => {
    setTpFilter("");
    setTruckFilter("");
  };

  const activeFilterCount = [tpFilter, truckFilter].filter(f => f).length;

  const handleExportExcel = () => {
    const exportData = data.map((row, i) => ({
      "S.No": i + 1,
      "LR ID": row.freight_id,
      "Party Name": row.party_name,
      "Loading Date": formatDate(row.loading_date),
      "Unloading Date": formatDate(row.unloading_date),
      "Truck No.": row.truck_no,
      "D.O. No.": row.do_no,
      "T.P. No.": row.tp_no,
      From: row.from_location,
      To: row.to_location,
      "Loading Wt": row.loading_wt,
      "Receiving Wt": row.received_wt,
      "Shortage Wt": row.shortage_wt,
      "Billing Wt": row.billing_wt,
      "LR Rate": row.freight_rate,
      "LR Amt": row.freight_amt,
      "TDS %": row.tds_per,
      "TDS Amt": row.tds_amt,
      "Shortage Rate": row.shortage_rate,
      "Shortage Amt": row.shortage_amt,
      "Ad Mines": row.ad_mines_exp,
      "Ad Diesel": row.ad_diesel_exp,
      "Ad Cash": row.ad_cash_exp,
      "Total Ad": row.total_advance,
      Commission: row.commission,
      "Net Amt": row.net_amount,
      "Others Exp.": row.other_exp,
      "Net Payable": row.net_payable,
      "Paid Amt": row.paid_amount,
      "Bal Amt": row.bal_amount,
      "Truck Owner": row.truck_owner,
      "Phone No.": row.phone_no,
      "PAN No.": row.pan_no,
      Remark: row.remark,
      "Rec Amt": row.receive_amount,
      "Diff Amt": row.difference_amount,
      "Payment Remark": row.payment_remark,
      Date: formatDate(row.created_at),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Branch LR's");
    XLSX.writeFile(wb, `Branch_Freights_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head><title>Branch LR's</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          h2, h4 { text-align: center; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
        </style></head>
        <body>
          <h2>PARAS ROAD LINES</h2>
          <h4>Branch LR's - ${selectedBranch ? branchOptions.find(b => b.branch_id === selectedBranch)?.branch_name || "" : ""}</h4>
          <table>
            <thead><tr><th>S.No</th><th>LR ID</th><th>Party Name</th><th>Loading Date</th><th>From</th><th>To</th><th>Net Payable</th></tr></thead>
            <tbody>
              ${data.map((row, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${row.freight_id || "-"}</td>
                  <td>${row.party_name || "-"}</td>
                  <td>${formatDate(row.loading_date)}</td>
                  <td>${row.from_location || "-"}</td>
                  <td>${row.to_location || "-"}</td>
                  <td>₹${row.net_payable || 0}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  const totals = {
    loading_wt: data.reduce((s, c) => s + (+c.loading_wt || 0), 0).toFixed(3),
    received_wt: data.reduce((s, c) => s + (+c.received_wt || 0), 0).toFixed(3),
    shortage_wt: data.reduce((s, c) => s + (+c.shortage_wt || 0), 0).toFixed(3),
    billing_wt: data.reduce((s, c) => s + (+c.billing_wt || 0), 0).toFixed(3),
    freight_amt: data.reduce((s, c) => s + (+c.freight_amt || 0), 0).toFixed(1),
    tds_amt: data.reduce((s, c) => s + (+c.tds_amt || 0), 0).toFixed(1),
    shortage_amt: data.reduce((s, c) => s + (+c.shortage_amt || 0), 0).toFixed(1),
    total_advance: data.reduce((s, c) => s + (+c.total_advance || 0), 0).toFixed(1),
    commission: data.reduce((s, c) => s + (+c.commission || 0), 0).toFixed(1),
    net_amount: data.reduce((s, c) => s + (+c.net_amount || 0), 0).toFixed(1),
    other_exp: data.reduce((s, c) => s + (+c.other_exp || 0), 0).toFixed(1),
    net_payable: data.reduce((s, c) => s + (+c.net_payable || 0), 0).toFixed(1),
    paid_amount: data.reduce((s, c) => s + (+c.paid_amount || 0), 0).toFixed(1),
    bal_amount: data.reduce((s, c) => s + (+c.bal_amount || 0), 0).toFixed(1),
  };

  const truckOptions = [...new Set(data.map(i => i.truck_no).filter(Boolean))];

  return (
    <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Paper sx={{ borderRadius: 2, border: "1px solid #e0e0e0", overflow: "hidden", mb: 2 }}>
        <Box sx={{ bgcolor: brand, px: 3, py: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <BusinessIcon sx={{ color: "#fff", fontSize: 22 }} />
              <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: "0.9rem" }}>Branch-wise LR's</Typography>
              <Chip label={`${data.length} Records`} size="small" sx={{ bgcolor: alpha("#fff", 0.2), color: "#fff", borderRadius: 1 }} />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Print"><IconButton size="small" onClick={handlePrint} sx={{ bgcolor: alpha("#fff", 0.1), color: "#fff", borderRadius: 1 }}><PrintIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
              <Tooltip title="Export Excel"><IconButton size="small" onClick={handleExportExcel} sx={{ bgcolor: alpha("#fff", 0.1), color: "#fff", borderRadius: 1 }}><DownloadIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            </Stack>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={4}><Paper sx={{ p: 1.5, textAlign: "center", borderRadius: 2, bgcolor: alpha(brand, 0.08), border: `1px solid ${alpha(brand, 0.2)}` }}>
          <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>Total Records</Typography>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: brand }}>{data.length}</Typography>
        </Paper></Grid>
        <Grid item xs={4}><Paper sx={{ p: 1.5, textAlign: "center", borderRadius: 2, bgcolor: alpha("#dc2626", 0.08), border: `1px solid ${alpha("#dc2626", 0.2)}` }}>
          <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>Total Amount</Typography>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#dc2626" }}>{formatCurrency(totals.net_payable)}</Typography>
        </Paper></Grid>
        <Grid item xs={4}><Paper sx={{ p: 1.5, textAlign: "center", borderRadius: 2, bgcolor: alpha("#10b981", 0.08), border: `1px solid ${alpha("#10b981", 0.2)}` }}>
          <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>Balance</Typography>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#10b981" }}>{formatCurrency(totals.bal_amount)}</Typography>
        </Paper></Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ borderRadius: 2, border: "1px solid #e0e0e0", overflow: "hidden", mb: 2, p: 1.5 }}>
        {isMobile && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Filters</Typography>
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ color: brand }}>
              {showFilters ? <CloseIcon sx={{ fontSize: 18 }} /> : <FilterListIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        )}
        <Collapse in={!isMobile || showFilters}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={branchOptions}
                getOptionLabel={(option) => option?.branch_name || ""}
                value={branchOptions.find(b => b.branch_id === selectedBranch) || null}
                onChange={(event, newValue) => handleBranchChange(newValue?.branch_id || null)}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Select Branch" InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ fontSize: 18, color: "#999" }} /></InputAdornment> }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" placeholder="TP No." value={tpFilter} onChange={(e) => setTpFilter(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} InputProps={{ startAdornment: <InputAdornment position="start"><ReceiptIcon sx={{ fontSize: 18, color: "#999" }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete freeSolo options={truckOptions} value={truckFilter} onInputChange={(e, v) => setTruckFilter(v || "")} renderInput={(params) => <TextField {...params} size="small" placeholder="Truck No." InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start"><TruckIcon sx={{ fontSize: 18, color: "#999" }} /></InputAdornment> }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button fullWidth variant="outlined" size="small" onClick={clearFilters} sx={{ borderRadius: 1, textTransform: "none" }}>Clear Filters</Button>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Table - Branch Name column removed since filter already shows branch */}
      <Paper sx={{ borderRadius: 2, border: "1px solid #e0e0e0", overflow: "hidden" }}>
        <Box id="scrollableDiv" sx={{ maxHeight: "calc(100vh - 340px)", overflow: "auto" }}>
          <InfiniteScroll dataLength={data.length} next={fetchMoreData} hasMore={hasMore} loader={<Box sx={{ textAlign: "center", py: 2 }}><CircularProgress size={24} sx={{ color: brand }} /></Box>} scrollableTarget="scrollableDiv">
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table stickyHeader size="small" id="freight-list">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem", position: "sticky", left: 0, zIndex: 2 }}>S.No</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem", position: "sticky", left: 40, zIndex: 2 }}>LR ID</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem", position: "sticky", left: 100, zIndex: 2 }}>Truck No.</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Party Name</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Loading Date</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Unloading Date</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>D.O. No.</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>T.P. No.</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>From</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>To</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Loading Wt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Receiving Wt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Shortage Wt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Billing Wt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>LR Rate</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>LR Amt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>TDS %</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>TDS Amt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Shortage Rate</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Shortage Amt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Ad Mines</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Ad Diesel</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Ad Cash</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Total Ad</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Commission</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Net Amt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Others Exp.</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Net Payable</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Paid Amt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Bal Amt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Truck Owner</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Phone No.</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>PAN No.</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Remark</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Rec Amt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Diff Amt</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Payment Remark</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", fontSize: "0.65rem" }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && data.length === 0 ? (
                    <TableRow><TableCell colSpan={38} align="center" sx={{ py: 5 }}><CircularProgress sx={{ color: brand }} /></TableCell></TableRow>
                  ) : data.length === 0 ? (
                    <TableRow><TableCell colSpan={38} align="center" sx={{ py: 5 }}>
                      {selectedBranch ? "No records found" : "Please select a branch"}
                    </TableCell></TableRow>
                  ) : (
                    data.map((row, idx) => (
                      <TableRow key={row.freight_id || idx} hover sx={{ bgcolor: row.is_remember ? alpha("#d97706", 0.1) : idx % 2 === 0 ? "#fafafa" : "#fff" }}>
                        <TableCell sx={{ position: "sticky", left: 0, bgcolor: "inherit", zIndex: 1 }}>{idx + 1}</TableCell>
                        <TableCell sx={{ position: "sticky", left: 40, bgcolor: "inherit", zIndex: 1 }}><Chip label={row.freight_id} size="small" sx={{ bgcolor: brandBg, color: brand, borderRadius: 1 }} /></TableCell>
                        <TableCell sx={{ position: "sticky", left: 100, bgcolor: "inherit", zIndex: 1 }}><Typography sx={{ fontWeight: 600, color: brand }}>{row.truck_no || "-"}</Typography></TableCell>
                        <TableCell>{row.party_name || "-"}</TableCell>
                        <TableCell>{formatDate(row.loading_date)}</TableCell>
                        <TableCell>{formatDate(row.unloading_date)}</TableCell>
                        <TableCell>{row.do_no || "-"}</TableCell>
                        <TableCell>{row.tp_no || "-"}</TableCell>
                        <TableCell>{row.from_location || "-"}</TableCell>
                        <TableCell>{row.to_location || "-"}</TableCell>
                        <TableCell>{row.loading_wt}</TableCell>
                        <TableCell>{row.received_wt}</TableCell>
                        <TableCell sx={{ color: row.shortage_wt > 0 ? "#dc2626" : "inherit" }}>{row.shortage_wt}</TableCell>
                        <TableCell>{row.billing_wt}</TableCell>
                        <TableCell>₹{row.freight_rate}</TableCell>
                        <TableCell>₹{row.freight_amt}</TableCell>
                        <TableCell>{row.tds_per}%</TableCell>
                        <TableCell>₹{row.tds_amt}</TableCell>
                        <TableCell>₹{row.shortage_rate}</TableCell>
                        <TableCell sx={{ color: "#dc2626" }}>₹{row.shortage_amt}</TableCell>
                        <TableCell>₹{row.ad_mines_exp}</TableCell>
                        <TableCell>₹{row.ad_diesel_exp}</TableCell>
                        <TableCell>₹{row.ad_cash_exp}</TableCell>
                        <TableCell>₹{row.total_advance}</TableCell>
                        <TableCell>₹{row.commission}</TableCell>
                        <TableCell>₹{row.net_amount}</TableCell>
                        <TableCell>₹{row.other_exp}</TableCell>
                        <TableCell><Typography sx={{ fontWeight: 700, color: brand }}>₹{row.net_payable}</Typography></TableCell>
                        <TableCell>₹{row.paid_amount?.toFixed(1)}</TableCell>
                        <TableCell>₹{row.bal_amount?.toFixed(1)}</TableCell>
                        <TableCell>{row.truck_owner}</TableCell>
                        <TableCell>{row.phone_no}</TableCell>
                        <TableCell>{row.pan_no}</TableCell>
                        <TableCell><Tooltip title={row.remark} arrow><Typography sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.remark || "-"}</Typography></Tooltip></TableCell>
                        <TableCell>{row.receive_amount?.toFixed(1)}</TableCell>
                        <TableCell>{row.difference_amount?.toFixed(1)}</TableCell>
                        <TableCell><Tooltip title={row.payment_remark} arrow><Typography sx={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.payment_remark || "-"}</Typography></Tooltip></TableCell>
                        <TableCell>{formatDate(row.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                {/* Total Row */}
                {data.length > 0 && (
                  <TableRow sx={{ bgcolor: brandBg, "& td": { fontWeight: "bold", fontSize: "0.7rem" } }}>
                    <TableCell colSpan={10} align="right">Total:</TableCell>
                    <TableCell>{totals.loading_wt}</TableCell><TableCell>{totals.received_wt}</TableCell>
                    <TableCell>{totals.shortage_wt}</TableCell><TableCell>{totals.billing_wt}</TableCell>
                    <TableCell></TableCell><TableCell>{formatCurrency(totals.freight_amt)}</TableCell>
                    <TableCell></TableCell><TableCell>{formatCurrency(totals.tds_amt)}</TableCell>
                    <TableCell></TableCell><TableCell>{formatCurrency(totals.shortage_amt)}</TableCell>
                    <TableCell colSpan={2}></TableCell><TableCell></TableCell>
                    <TableCell>{formatCurrency(totals.total_advance)}</TableCell>
                    <TableCell>{formatCurrency(totals.commission)}</TableCell>
                    <TableCell>{formatCurrency(totals.net_amount)}</TableCell>
                    <TableCell>{formatCurrency(totals.other_exp)}</TableCell>
                    <TableCell sx={{ color: brand, fontWeight: "bold" }}>{formatCurrency(totals.net_payable)}</TableCell>
                    <TableCell>{formatCurrency(totals.paid_amount)}</TableCell>
                    <TableCell>{formatCurrency(totals.bal_amount)}</TableCell>
                    <TableCell colSpan={7}></TableCell>
                  </TableRow>
                )}
              </Table>
            </TableContainer>
          </InfiniteScroll>
        </Box>
      </Paper>
    </Box>
  );
}